<template>
  <header class="sticky top-0 z-50">
    <!-- Announcement banner: full-width secondary (mint) strip -->
    <div v-if="bannerOpen" class="bg-secondary text-primary">
      <div class="max-w-page mx-auto px-6 py-2.5 flex items-center justify-center gap-x-4 relative">
        <p class="text-sm font-medium text-center">
          10.000 TL'den başlayan tokenize gayrimenkul yatırımı yakında.
          <router-link to="/#waitlist" class="underline font-semibold ml-2">
            Erken erişime kayıt ol <span aria-hidden="true">→</span>
          </router-link>
        </p>
        <button
          @click="dismissBanner"
          aria-label="Kapat"
          class="absolute right-4 top-1/2 -translate-y-1/2 w-6 h-6 flex items-center justify-center text-primary hover:opacity-70"
        >
          <svg class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
    </div>

    <!-- Sticky navigation bar -->
    <nav
      class="border-b border-stone backdrop-blur transition-colors"
      :class="scrolled ? 'bg-cream/90' : 'bg-cream'"
    >
      <div class="max-w-page mx-auto px-6 h-16 md:h-[72px] flex items-center justify-between gap-6">
        <!-- Logo -->
        <router-link to="/" class="flex items-center shrink-0 h-full">
          <img class="block h-7 md:h-8 w-auto" src="@/assets/logo.png" alt="Bloqin" />
        </router-link>

        <!-- Center nav links (desktop) -->
        <ul class="hidden lg:flex items-center gap-1 text-sm font-medium text-primary">
          <li>
            <router-link to="/#features" class="px-3 py-2 rounded-btn hover:bg-parchment transition-colors">
              Yatırımlar
            </router-link>
          </li>
          <li>
            <router-link to="/#how" class="px-3 py-2 rounded-btn hover:bg-parchment transition-colors">
              Nasıl Çalışır
            </router-link>
          </li>
          <li>
            <router-link to="/#roadmap" class="px-3 py-2 rounded-btn hover:bg-parchment transition-colors">
              Yol Haritası
            </router-link>
          </li>
          <li>
            <router-link to="/about" class="px-3 py-2 rounded-btn hover:bg-parchment transition-colors">
              Hakkımızda
            </router-link>
          </li>
          <li>
            <router-link to="/apply" class="px-3 py-2 rounded-btn hover:bg-parchment transition-colors">
              Birlikte Çalışalım
            </router-link>
          </li>
        </ul>

        <!-- Right cluster -->
        <div class="flex items-center gap-2">
          <!-- Globe icon button + language dropdown -->
          <div class="relative hidden md:block" ref="langMenuRef">
            <button
              @click="langOpen = !langOpen"
              aria-label="Dil seçimi"
              :aria-expanded="langOpen"
              class="w-10 h-10 rounded-full border border-stone flex items-center justify-center text-primary hover:bg-parchment transition-colors"
              :class="langOpen ? 'bg-parchment' : ''"
            >
              <svg class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="1.5" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" d="M12 21a9 9 0 100-18 9 9 0 000 18z" />
                <path stroke-linecap="round" stroke-linejoin="round" d="M3.6 9h16.8M3.6 15h16.8M12 3a13.5 13.5 0 010 18M12 3a13.5 13.5 0 000 18" />
              </svg>
            </button>

            <transition
              enter-active-class="transition ease-out duration-150"
              enter-from-class="opacity-0 translate-y-1"
              enter-to-class="opacity-100 translate-y-0"
              leave-active-class="transition ease-in duration-100"
              leave-from-class="opacity-100"
              leave-to-class="opacity-0"
            >
              <div
                v-if="langOpen"
                class="absolute right-0 mt-2 w-44 rounded-card bg-cream border border-stone p-1 z-50"
                role="menu"
              >
                <button
                  type="button"
                  role="menuitem"
                  class="w-full flex items-center justify-between gap-2 px-3 py-2 rounded-btn bg-parchment text-primary text-sm font-medium"
                >
                  <span>Türkçe</span>
                  <svg class="w-4 h-4 text-secondary" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                </button>
                <button
                  type="button"
                  role="menuitem"
                  disabled
                  class="w-full flex items-center justify-between gap-2 px-3 py-2 rounded-btn text-graphite text-sm cursor-not-allowed"
                >
                  <span>English</span>
                  <span class="text-xs tracking-label uppercase">Yakında</span>
                </button>
              </div>
            </transition>
          </div>

          <!-- Ghost button: Yatırımcı Paneli -->
          <router-link
            to="/login"
            class="hidden sm:inline-flex items-center px-4 py-2.5 rounded-btn border border-primary text-primary text-sm font-medium hover:bg-primary hover:text-cream transition-colors"
          >
            Yatırımcı Paneli
          </router-link>

          <!-- Filled CTA: Bekleme listesi -->
          <router-link
            to="/#waitlist"
            class="inline-flex items-center px-4 py-2.5 rounded-btn border border-primary bg-secondary text-primary text-sm font-medium hover:bg-primary hover:text-secondary transition-colors"
          >
            Bekleme Listesi
          </router-link>

          <!-- Mobile menu toggle -->
          <button
            @click="mobileOpen = !mobileOpen"
            aria-label="Menü"
            class="lg:hidden w-10 h-10 rounded-full border border-stone flex items-center justify-center text-primary hover:bg-parchment transition-colors"
          >
            <svg v-if="!mobileOpen" class="w-5 h-5" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" d="M4 6h16M4 12h16M4 18h16" />
            </svg>
            <svg v-else class="w-5 h-5" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      </div>

      <!-- Mobile menu -->
      <div v-if="mobileOpen" class="lg:hidden border-t border-stone bg-cream">
        <ul class="max-w-page mx-auto px-6 py-4 flex flex-col gap-1 text-base font-medium text-primary">
          <li><router-link @click="mobileOpen = false" to="/#features" class="block px-3 py-3 rounded-btn hover:bg-parchment">Yatırımlar</router-link></li>
          <li><router-link @click="mobileOpen = false" to="/#how" class="block px-3 py-3 rounded-btn hover:bg-parchment">Nasıl Çalışır</router-link></li>
          <li><router-link @click="mobileOpen = false" to="/#roadmap" class="block px-3 py-3 rounded-btn hover:bg-parchment">Yol Haritası</router-link></li>
          <li><router-link @click="mobileOpen = false" to="/about" class="block px-3 py-3 rounded-btn hover:bg-parchment">Hakkımızda</router-link></li>
          <li><router-link @click="mobileOpen = false" to="/apply" class="block px-3 py-3 rounded-btn hover:bg-parchment">Birlikte Çalışalım</router-link></li>
          <li class="pt-2 mt-2 border-t border-stone">
            <router-link @click="mobileOpen = false" to="/login" class="block px-3 py-3 rounded-btn hover:bg-parchment">Yatırımcı Paneli</router-link>
          </li>
        </ul>
      </div>
    </nav>
  </header>
</template>

<script lang="ts">
import { defineComponent, ref, onMounted, onUnmounted } from 'vue';
import { useRouter } from 'vue-router';
import { useAuthStore } from '../stores/auth';

const BANNER_STORAGE_KEY = 'bloqin_banner_dismissed';

export default defineComponent({
  setup() {
    const authStore = useAuthStore();
    const router = useRouter();
    const bannerOpen = ref(false);
    const mobileOpen = ref(false);
    const scrolled = ref(false);
    const langOpen = ref(false);
    const langMenuRef = ref<HTMLElement | null>(null);

    const dismissBanner = () => {
      bannerOpen.value = false;
      try {
        localStorage.setItem(BANNER_STORAGE_KEY, '1');
      } catch {
        /* localStorage unavailable — silent fail */
      }
    };

    const onScroll = () => {
      scrolled.value = window.scrollY > 8;
    };

    const onClickOutside = (e: MouseEvent) => {
      if (!langOpen.value) return;
      if (langMenuRef.value && !langMenuRef.value.contains(e.target as Node)) {
        langOpen.value = false;
      }
    };

    const onKeydown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') langOpen.value = false;
    };

    onMounted(() => {
      try {
        bannerOpen.value = localStorage.getItem(BANNER_STORAGE_KEY) !== '1';
      } catch {
        bannerOpen.value = true;
      }
      window.addEventListener('scroll', onScroll, { passive: true });
      document.addEventListener('click', onClickOutside);
      document.addEventListener('keydown', onKeydown);
    });

    onUnmounted(() => {
      window.removeEventListener('scroll', onScroll);
      document.removeEventListener('click', onClickOutside);
      document.removeEventListener('keydown', onKeydown);
    });

    router.afterEach(() => {
      mobileOpen.value = false;
      langOpen.value = false;
    });

    return {
      authStore,
      bannerOpen,
      mobileOpen,
      scrolled,
      langOpen,
      langMenuRef,
      dismissBanner,
    };
  },
});
</script>
